import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Box from '../atoms/Box';
import Text from '../atoms/Text';
import { useTranslate } from '../../translate/I18n';
import ThreeStatesCheckBox from './ThreeStatesCheckBox';
import Location from '../atoms/icons/Location';
import ChevronDown from '../atoms/icons/ChevronDown';
import LocationsDropdownPresenter, { ACTION } from './mvp/LocationsDropdownPresenter';
import { withMVP } from '../../hoc/mvp';
import { LocationPreferencesType } from '../profile/ProfileInfo.type';
import { isEmpty } from 'ramda';

/**
 * @param {import('mvp/LocationsDropdownPresenter')} presenter
 * @param {import('mvp/type').ViewModel} viewModel
 * @param {([]) => void} onChange
 * @returns {JSX.Element}
 * @constructor
 */
export const LocationsDropdown = ({ onChange, presenter, viewModel }) => {
  const { t } = useTranslate(['locations']);
  const dropdownRef = useRef();
  const [openList, setOpenList] = useState(false);

  useEffect(() => {
    /**
     * @param {React.MouseEvent<HTMLElement>} e
     */
    const handleClick = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && openList) {
        closeList();
      }
    };

    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [dropdownRef, openList]);

  const translate = useCallback(
    /**
     * @param {import("./type").LocationCountryOrRegion} location
     */
    location => {
      const key = presenter.getTranslatedKey(location);

      const isARegionFromOtherCountries = !location.isCountry() && location.isOtherCountry();

      return isARegionFromOtherCountries ? key : t(`locations:${key}`);
    },
    [t, presenter]
  );

  const placeholder = useMemo(() => {
    const generatedPlaceholder = presenter.getCheckedLocations().map(location => translate(location));
    return !isEmpty(generatedPlaceholder) ? generatedPlaceholder.join(', ') : t('mission:filterByLocation');
  }, [presenter, translate, t]);

  const toggleList = e => {
    e.stopPropagation();
    setOpenList(!openList);
  };

  const closeList = () => {
    setOpenList(false);
  };

  /**
   * @param {string} locationName
   * @param {ACTION.TOGGLE_COUNTRY | ACTION.TOGGLE_REGION} action
   */
  const onChangeLocation = (locationName, action) => {
    presenter.dispatch(locationName, action);
    onChange(presenter.getCheckedLocationsAsString(true));
  };

  return (
    <Box position="relative" ref={dropdownRef}>
      <Box
        bg="white"
        border="1px solid"
        borderColor="greyVerylight"
        borderRadius={2}
        cursor="pointer"
        overflow="hidden"
        pl={2}
        py={'9px'}
        onClick={e => toggleList(e)}>
        <Box display="flex">
          <Box display="flex" alignItems="center" mr={1}>
            <Location />
          </Box>
          <Text data-cy="dropdown-content" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" pr={3}>
            {placeholder}
          </Text>
        </Box>
        <Box position="absolute" right={1} top="50%" transform="translateY(-50%)" pt="6px">
          <ChevronDown fontSize="18px" />
        </Box>
      </Box>
      <Box
        display={openList ? 'block' : 'none'}
        width="100%"
        bg="white"
        borderRadius={2}
        boxShadow="0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.1)"
        p={2}
        position="absolute"
        maxHeight="330px"
        overflowY="scroll"
        top="calc(100% + 2px)"
        zIndex="10">
        <Box>
          {viewModel.state.map((location, locationIndex) => {
            return (
              <React.Fragment key={location.slug + locationIndex}>
                <CountryComponent
                  name={translate(location)}
                  nbMissions={location.nbMissions}
                  value={presenter.getCheckboxStatus(location.slug)}
                  onChange={() => onChangeLocation(location.slug, ACTION.TOGGLE_COUNTRY)}
                />
                {location.regions.map(
                  (region, regionIndex) =>
                    region.name && (
                      <RegionComponent
                        key={location.slug + locationIndex + region.slug + regionIndex}
                        name={translate(region)}
                        nbMissions={region.nbMissions}
                        value={presenter.getCheckboxStatus(location.slug, region.slug)}
                        onChange={() => onChangeLocation(region.slug, ACTION.TOGGLE_REGION)}
                      />
                    )
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

LocationsDropdown.propTypes = {
  viewModel: PropTypes.shape({
    state: PropTypes.arrayOf(LocationPreferencesType)
  }),
  presenter: PropTypes.shape({
    getCheckboxStatus: PropTypes.func,
    getCheckedLocations: PropTypes.func,
    getTranslatedKey: PropTypes.func,
    dispatch: PropTypes.func,
    payload: PropTypes.func,
    getCheckedLocationsAsString: PropTypes.func
  }),
  onChange: PropTypes.func
};

const LocationComponent = ({ value, onChange, name, nbMissions }) => (
  <ThreeStatesCheckBox
    value={value}
    onChange={onChange}
    label={
      <p>
        {name}
        <Text as="span" ml={1} color="grey">
          {nbMissions}
        </Text>
      </p>
    }
  />
);

LocationComponent.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  nbMissions: PropTypes.number
};

const CountryComponent = props => (
  <Box mb={2} display="block">
    <LocationComponent {...props} />
  </Box>
);

const RegionComponent = props => (
  <Box mb={2} ml={3} display="flex">
    <LocationComponent {...props} />
  </Box>
);

const makeLocationDropdownPresenter = () => new LocationsDropdownPresenter();

const useDynamicDependencies = props => ({ ...props });

export default withMVP(makeLocationDropdownPresenter, useDynamicDependencies)(LocationsDropdown);
